/**
 * Export Utilities
 *
 * Client-side generation of Excel-compatible CSV and EML email files
 * for the USI/HMC Events feature. Handles proper CSV escaping,
 * BOM for Excel compatibility, and RFC 822 email format.
 *
 * Used by:
 * - GuestListPreview (download guest list / email file)
 * - USI Events feature
 * - HMC Events feature
 */

// ============================================================
// TYPES
// ============================================================

/**
 * Guest/recipient data for export
 */
export interface ExportGuest {
  name: string;
  email: string;
  organization: string;
  title?: string | null;
  personId?: number;
}

// ============================================================
// CSV GENERATION
// ============================================================

/**
 * Escape a CSV field value per RFC 4180.
 * Wraps in double-quotes and escapes embedded quotes
 * when the field contains commas, quotes, or newlines.
 */
function escapeCsvField(field: string): string {
  if (field.includes(',') || field.includes('"') || field.includes('\n')) {
    return `"${field.replace(/"/g, '""')}"`;
  }
  return field;
}

/**
 * Generate a CSV file (Excel-compatible) from a guest list.
 * Returns a Blob that can be downloaded via {@link downloadBlob}.
 *
 * The CSV includes a UTF-8 BOM (\uFEFF) so that Excel on Windows
 * correctly interprets accented / Unicode characters.
 *
 * @param guests - Array of guest records to include
 * @param _eventName - Reserved for future use (e.g. title row)
 */
export function generateGuestListCsv(
  guests: ExportGuest[],
  _eventName?: string,
): Blob {
  const headers = ['Name', 'Email', 'Organization', 'Title'];

  const rows = guests.map((g) => [
    escapeCsvField(g.name),
    escapeCsvField(g.email),
    escapeCsvField(g.organization),
    escapeCsvField(g.title || ''),
  ]);

  const csv = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');

  // BOM prefix ensures Excel opens as UTF-8
  return new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
}

// ============================================================
// EML GENERATION
// ============================================================

/**
 * Generate an .eml file (RFC 822) from email content.
 * Returns a Blob that can be downloaded and opened in any email client.
 *
 * @param subject    - Email subject line
 * @param htmlBody   - HTML content for the email body
 * @param recipients - Array of { name, email } recipient objects
 * @param fromName   - Sender display name (defaults to "Benefits Outreach")
 * @param fromEmail  - Sender email address (defaults to noreply@example.com)
 */
export function generateEmailFile(
  subject: string,
  htmlBody: string,
  recipients: Array<{ name: string; email: string }>,
  fromName?: string,
  fromEmail?: string,
): Blob {
  const to = recipients.map((r) => `${r.name} <${r.email}>`).join(', ');
  const boundary = `----=_Part_${Date.now()}`;

  const eml = [
    `From: ${fromName || 'Benefits Outreach'} <${fromEmail || 'noreply@example.com'}>`,
    `To: ${to}`,
    `Subject: ${subject}`,
    `MIME-Version: 1.0`,
    `Content-Type: multipart/alternative; boundary="${boundary}"`,
    ``,
    `--${boundary}`,
    `Content-Type: text/html; charset=utf-8`,
    `Content-Transfer-Encoding: quoted-printable`,
    ``,
    htmlBody,
    ``,
    `--${boundary}--`,
  ].join('\r\n');

  return new Blob([eml], { type: 'message/rfc822' });
}

// ============================================================
// DOWNLOAD HELPER
// ============================================================

/**
 * Trigger a browser download of a Blob by creating a temporary anchor element.
 *
 * @param blob     - The file content as a Blob
 * @param filename - Suggested file name for the download
 */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
